/**
 * Storage utilities for persisting application state
 */

const STORAGE_KEYS = {
    TRADING_TABS: 'quantix_trading_tabs',
    ACTIVE_TAB: 'quantix_active_tab',
} as const;

export interface TradingTab {
    id: string;
    instrumentId: string; // Changed from number to string to match UUID format
    symbol: string; // Keep symbol for display purposes
}

export interface TradingTabsState {
    tabs: TradingTab[];
    activeTab: string;
}

/**
 * Save trading tabs state to localStorage
 */
export const saveTradingTabsState = (state: TradingTabsState): void => {
    try {
        localStorage.setItem(STORAGE_KEYS.TRADING_TABS, JSON.stringify(state.tabs));
        localStorage.setItem(STORAGE_KEYS.ACTIVE_TAB, state.activeTab);
    } catch (error) {
        console.warn('Failed to save trading tabs state to localStorage:', error);
    }
};

/**
 * Load trading tabs state from localStorage
 */
export const loadTradingTabsState = (): TradingTabsState | null => {
    try {
        const tabsJson = localStorage.getItem(STORAGE_KEYS.TRADING_TABS);
        const activeTab = localStorage.getItem(STORAGE_KEYS.ACTIVE_TAB);

        if (!tabsJson || !activeTab) {
            return null;
        }

        const tabs = JSON.parse(tabsJson) as TradingTab[];

        // Validate the loaded data
        if (!Array.isArray(tabs) || tabs.length === 0) {
            clearTradingTabsState();
            return null;
        }

        // Ensure the active tab exists in the tabs array
        const activeTabExists = tabs.some(tab => tab.id === activeTab);
        if (!activeTabExists) {
            clearTradingTabsState();
            return null;
        }

        return { tabs, activeTab };
    } catch (error) {
        console.warn('Failed to load trading tabs state from localStorage:', error);
        return null;
    }
};

/**
 * Validate and clean up trading tabs against available instruments
 */
export const validateAndCleanTradingTabs = (
    savedState: TradingTabsState | null,
    availableInstruments: any[]
): TradingTabsState | null => {
    if (!savedState) return null;

    // Get available instrument IDs (they are strings/UUIDs)
    const availableInstrumentIds = new Set(availableInstruments.map(inst => inst.id));

    // Filter out tabs with instrument IDs that no longer exist
    const validTabs = savedState.tabs.filter(tab => availableInstrumentIds.has(tab.instrumentId));

    if (validTabs.length === 0) {
        return null; // No valid tabs left
    }

    // Check if active tab is still valid
    const activeTabValid = validTabs.some(tab => tab.id === savedState.activeTab);

    const result = {
        tabs: validTabs,
        activeTab: activeTabValid ? savedState.activeTab : validTabs[0].id,
    };

    // Log if validation resulted in changes
    if (validTabs.length !== savedState.tabs.length || !activeTabValid) {
        console.log('Trading tabs cleaned up:', {
            original: savedState,
            cleaned: result,
            removedTabs: savedState.tabs.filter(
                tab => !availableInstrumentIds.has(tab.instrumentId)
            ),
        });
    }

    return result;
};

/**
 * Clear trading tabs state from localStorage
 */
export const clearTradingTabsState = (): void => {
    try {
        localStorage.removeItem(STORAGE_KEYS.TRADING_TABS);
        localStorage.removeItem(STORAGE_KEYS.ACTIVE_TAB);
    } catch (error) {
        console.warn('Failed to clear trading tabs state from localStorage:', error);
    }
};

/**
 * Get default trading tabs state
 */
export const getDefaultTradingTabsState = (): TradingTabsState => {
    return {
        tabs: [
            {
                id: 'tab-1',
                instrumentId: '00000000-0000-0000-0000-000000000000',
                symbol: 'EUR/USD',
            },
        ],
        activeTab: 'tab-1',
    };
};
